import * as PIXI from 'pixi.js';
import type { ImageSource } from 'pixi.js';
import { WebpResource } from './WebpResource';

type UnderlyingPixiResource = PIXI.ImageResource | PIXI.VideoResource | PIXI.SVGResource;
type UnderlyingResource = UnderlyingPixiResource | WebpResource;

export type MediaResourceKind = 'video' | 'image' | 'svg' | 'webp';

export type MediaResourceCreateParams = {
    sourceUrl: string | null;
    sourceMimeType: 'video/mp4' | null;
    isVideo: boolean;
    isSvg: boolean;
    sourceDuration: number | null;
};

/**
 * Encapsulates a single media asset resource behind one interface, hiding which concrete
 * PIXI resource (or {@link WebpResource}) backs it. This lets MediaAsset operate on media
 * without branching on the resource/asset type in every method.
 */
export class MediaResource {
    #resource: UnderlyingResource;
    #kind: MediaResourceKind;

    constructor(resource: UnderlyingResource, kind: MediaResourceKind) {
        this.#resource = resource;
        this.#kind = kind;
    }

    /**
     * Create the appropriate resource for the given source. The selection order matches the
     * legacy logic: animated WebP is detected first (even for video source data), then video,
     * then SVG, then a plain image.
     */
    static create({
        sourceUrl,
        sourceMimeType,
        isVideo,
        isSvg,
        sourceDuration,
    }: MediaResourceCreateParams): MediaResource {
        if (WebpResource.isWebp(sourceUrl)) {
            if (!sourceUrl) {
                throw new Error('Cannot create webp resource without a source URL.');
            }
            return new MediaResource(new WebpResource(sourceUrl, sourceDuration), 'webp');
        }
        if (isVideo) {
            if (!sourceUrl) {
                throw new Error('Cannot create video resource without a source URL.');
            }
            if (!sourceMimeType) {
                throw new Error('Cannot create video resource without a source MIME type.');
            }
            return new MediaResource(
                MediaResource.#createVideoResource(sourceUrl, sourceMimeType),
                'video',
            );
        }
        if (!sourceUrl) {
            throw new Error('Cannot create image resource without a source URL.');
        }
        if (isSvg) {
            return new MediaResource(new PIXI.SVGResource(sourceUrl, {}), 'svg');
        }
        return new MediaResource(new PIXI.ImageResource(sourceUrl, { autoLoad: false }), 'image');
    }

    /**
     * CREATE VIDEO
     * NOTE: DO NOT mute before load, as Chrome bugs and creates a WebGL error
     *       https://bugs.chromium.org/p/chromium/issues/detail?id=898550
     *
     * Preloading:
     * The only reason we create the video element ourselves is to avoid PIXI setting preload="auto".
     * Preloading causes seeking to be super slow for some videos.
     * It requires more network and processing, especially with trimmed media, where
     * it seems that browsers try to load all the file instead of seeking.
     */
    static #createVideoResource(sourceUrl: string, sourceMimeType: string): PIXI.VideoResource {
        const videoElement = document.createElement('video');
        videoElement.setAttribute('preload', 'none');
        videoElement.setAttribute('webkit-playsinline', '');
        videoElement.setAttribute('playsinline', '');

        PIXI.BaseImageResource.crossOrigin(videoElement, sourceUrl);
        const sourceElement = document.createElement('source');
        sourceElement.src = sourceUrl;
        sourceElement.type = sourceMimeType;
        videoElement.appendChild(sourceElement);
        const resource = new PIXI.VideoResource(videoElement, {
            autoLoad: false,
            autoPlay: false,
        });
        resource.autoUpdate = false;
        videoElement.setAttribute('data-name-for-debugging', 'original');

        return resource;
    }

    /**
     * The underlying resource. Prefer the wrapper's own methods; use this only where the
     * concrete resource object is genuinely required.
     */
    get raw(): UnderlyingResource {
        return this.#resource;
    }

    get kind(): MediaResourceKind {
        return this.#kind;
    }

    get isVideo(): boolean {
        return this.#kind === 'video';
    }

    get isWebp(): boolean {
        return this.#kind === 'webp';
    }

    get isImage(): boolean {
        return this.#kind === 'image';
    }

    get isSvg(): boolean {
        return this.#kind === 'svg';
    }

    get source(): ImageSource | null {
        return this.#resource.source;
    }

    get width(): number {
        return this.#resource.width;
    }

    get height(): number {
        return this.#resource.height;
    }

    get valid(): boolean {
        return this.#resource.valid;
    }

    get destroyed(): boolean {
        return this.#resource.destroyed;
    }

    /**
     * Return the underlying resource's load promise directly rather than wrapping it in an
     * `async` method. An `async` wrapper adds an extra microtask before the caller's `.then`
     * runs, which shifts the resulting redraw into a later build pass and reintroduces the
     * watermark create/destroy loop (see watermark_tests "does not create a loop..."). Keep this
     * non-async to preserve the original rebuild timing.
     */
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    load(): Promise<unknown> {
        return this.#resource.load();
    }

    destroy(): void {
        this.#resource.destroy();
    }

    /**
     * The backing {@link HTMLVideoElement} when this resource is video-backed, otherwise null.
     * WebP resources are never considered video-backed even though they animate.
     */
    get videoElement(): HTMLVideoElement | null {
        if (this.#resource instanceof WebpResource) {
            return null;
        }
        const source = this.#resource.source;
        return source instanceof HTMLVideoElement ? source : null;
    }

    /**
     * Swap the backing video element. Used to switch the active playback region's video.
     */
    set videoElement(videoElement: HTMLVideoElement) {
        if (this.#resource instanceof WebpResource) {
            throw new Error('Cannot set a video element on a non-video resource.');
        }
        this.#resource.source = videoElement;
    }

    /**
     * Move the current frame to the given time. Only meaningful for WebP; a no-op otherwise
     * because video/image resources are seeked through their DOM element.
     */
    seek(time: number): void {
        if (this.#resource instanceof WebpResource) {
            this.#resource.seek(time);
        }
    }

    /**
     * Buffer the frame(s) needed for the given time. Only meaningful for WebP; a no-op
     * otherwise because video buffering is driven through the DOM element.
     */
    async buffer(time: number): Promise<void> {
        if (this.#resource instanceof WebpResource) {
            await this.#resource.buffer(time);
        }
    }
}
